<!--
  Checkout Page Component
  This component handles the checkout process, including payment, shipping, coupon codes, and order summary.
  It manages both guest and authenticated user flows, and integrates with Stripe for payment processing.
-->

<template>
  <!-- Root container for the checkout page, ensures full height -->
  <div class="h-100">
    <!-- Overlay shown during payment processing to block user interaction -->
    <!-- Binds overlay visibility to payment processing state -->
    <!-- Dark background for overlay -->
    <v-overlay
      v-model="isPaymentProcessing"
      scrim="rgba(0,0,0,0.9)"
      class="payment-processing-overlay align-center justify-center"
      ref="divOne"
    >
      <v-container class="text-center">
        <v-row>
          <v-col cols="12" class="pa-0 pa-2">
            <!-- Circular progress indicator while payment is processing -->
            <v-progress-circular
              indeterminate
              size="65"
              color="white"
              align="center"
              justify="center"
              class="mx-auto"
            ></v-progress-circular>
          </v-col>
          <v-col cols="12" class="pa-0 pa-2">
            <!-- Message to prevent user from refreshing or navigating away during payment -->
            <div class="text-h5 text-white">
              Do not refresh or click the back button.
              <br />
              Your payment is currently being processed
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-overlay>

    <!-- Main checkout container -->
    <v-container fluid class="h-100 pa-0">
      <!-- Show checkout content only if user info and cart total are loaded -->
      <v-row
        no-gutters
        class="fill-height"
        v-if="Object.keys(userInfo).length > 0 && cartProductTotalAmt?.subtotal"
      >
        <!-- Mobile order summary bar, visible only on small screens -->
        <div class="d-block d-md-none w-100 mt-2" v-if="cartProductTotalAmt">
          <!-- Collapsible order summary bar for mobile -->
          <!-- Toggle summary open/close -->
          <div
            class="order-summary-bar"
            @click="isOrderSummaryOpen = !isOrderSummaryOpen"
          >
            <div class="order-summary-inner">
              <div style="display: flex; align-items: center">
                <span>Order Summary</span>
                <!-- Chevron icon rotates when summary is open -->
                <v-icon
                  :icon="mdiChevronDown"
                  :style="{
                    display: 'inline-block',
                    transition: 'transform 0.2s',
                    transform: isOrderSummaryOpen
                      ? 'rotate(180deg)'
                      : 'rotate(0deg)',
                    fontSize: '20px',
                    marginLeft: '4px',
                    color: '#444',
                  }"
                />
              </div>
              <!-- Display grand total -->
              <span style="font-weight: bold; font-size: 1.1rem; color: #222"
                >$ {{ formatAmount(cartProductTotalAmt.grandTotal) }}</span
              >
            </div>
          </div>
          <!-- Collapsible order summary content -->
          <div :class="['order-summary-content', { open: isOrderSummaryOpen }]">
            <div
              style="
                width: 100%;
                max-width: 580px;
                margin: 0 auto;
                padding: 0px 0px 10px;
                background-color: #fafafa;
              "
            >
              <!-- Product list for checkout, mobile view -->
              <CheckoutProductList
                :isUser="userAuth"
                :storeCreditUsed="storeCreditUsed"
                :giftCardCreditBalance="giftCardCreditBalance"
                :isCouponLoading="isCouponLoading"
                :discountGuestCode="discountGuestCode"
                :userDiscountCodeList="userDiscountCodeList"
                :checkoutProductList="cartProductsItem"
                :cartProductsProgress="cartProductsProgress"
                :isCheckoutProcessing="isCheckoutProcessing"
                :checkoutProductTotalAmt="cartProductTotalAmt"
                @checkGuestCouponCode="checkGuestCouponCode"
                @removeGuestCouponCode="removeGuestCouponCode"
                @checkUserCouponCode="checkUserCouponCode"
                @removeUserCouponCode="removeUserCouponCode"
                @useStoreCredit="useStoreCredit"
              />
            </div>
          </div>
        </div>
        <!-- Left column: payment and shipping form -->
        <v-col
          cols="12"
          :md="6"
          class="d-flex align- justify-center justify-lg-end pa-6"
        >
          <div class="w-100" style="max-width: 580px">
            <!-- Stripe payment form component -->
            <StripeCheckout
              ref="stripeCheckout"
              :storeCreditUsed="storeCreditUsed"
              :giftCardCreditBalance="giftCardCreditBalance"
              :userInfo="userInfo"
              :isGiftCardProduct="isGiftCardProduct"
              :estimatedShippingMethods="estimatedShippingMethods"
              :checkoutProductTotalAmt="cartProductTotalAmt"
              :isCheckoutProcessing="isCheckoutProcessing"
              :isUser="userAuth"
              :isCouponLoading="isCouponLoading"
              :discountGuestCode="discountGuestCode"
              :userDiscountCodeList="userDiscountCodeList"
              :avalaraAddressResponse="avalaraAddressResponse"
              @setShippingAddress="setShippingAddress"
              @shippingMethodChanged="setShippingInformation"
              @processPayment="processPayment"
              @handleExpressPaymentCompletion="handleExpressPaymentCompletion"
              @open-sign="openSign"
              @checkGuestCouponCode="checkGuestCouponCode"
              @removeGuestCouponCode="removeGuestCouponCode"
              @checkUserCouponCode="checkUserCouponCode"
              @removeUserCouponCode="removeUserCouponCode"
              @validateShippingAddress="validateShippingAddress"
              @useStoreCredit="useStoreCredit"
            />
          </div>
        </v-col>
        <!-- Right column: order summary, only visible on desktop -->
        <v-col
          cols="12"
          md="6"
          class="accent-3 pa-6 d-md-flex d-none"
          style="
            background-color: #fafafa;
            border-left: 1px solid rgb(223, 223, 223);
          "
        >
          <div class="checkout-right-column w-100" style="">
            <!-- Product list for checkout, desktop view -->
            <CheckoutProductList
              :isUser="userAuth"
              :storeCreditUsed="storeCreditUsed"
              :giftCardCreditBalance="giftCardCreditBalance"
              :isCouponLoading="isCouponLoading"
              :discountGuestCode="discountGuestCode"
              :userDiscountCodeList="userDiscountCodeList"
              :checkoutProductList="cartProductsItem"
              :cartProductsProgress="cartProductsProgress"
              :isCheckoutProcessing="isCheckoutProcessing"
              :checkoutProductTotalAmt="cartProductTotalAmt"
              @checkGuestCouponCode="checkGuestCouponCode"
              @removeGuestCouponCode="removeGuestCouponCode"
              @checkUserCouponCode="checkUserCouponCode"
              @removeUserCouponCode="removeUserCouponCode"
              @useStoreCredit="useStoreCredit"
            />
          </div>
        </v-col>
      </v-row>
      <!-- Show skeleton loader if user info or cart data is not loaded -->
      <v-row no-gutters class="fill-height" v-else>
        <CheckoutPageSkeleton></CheckoutPageSkeleton>
      </v-row>
      <!-- Error dialog for payment or checkout errors -->
      <v-dialog v-model="paymentError" persistent max-width="520">
        <v-card class="pa-6" elevation="3">
          <!-- Error title -->
          <v-card-title
            class="d-flex align-center pa-0 py-2 text-h5 font-weight-bold text-error"
          >
            {{ checkoutError?.title }}
          </v-card-title>
          <!-- Error message -->
          <v-card-text class="text-h6 pa-0">
            {{ checkoutError?.message }}
          </v-card-text>
          <!-- OK button to close dialog -->
          <v-card-actions class="justify-end mt-2">
            <v-btn
              color="primary"
              variant="flat"
              class="px-6 text-white"
              @click="closePaymentErrorDiloag"
            >
              OK
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
    <!-- Snackbar for transient error/success messages -->
    <SnackbarComponent :snackbar="snackbar"></SnackbarComponent>
    <v-dialog persistent v-model="signInForm" width="600">
      <v-card flat>
        <v-card-title class="d-flex justify-space-between">
          <p class="h3 mb-0">Sign In</p>
          <template v-if="!isAuthAccountInProgress">
            <a
              href="#"
              class="text-black mb-1"
              @click.prevent="closeSignInForm()"
            >
              <v-icon size="18" :icon="mdiWindowClose" />
            </a>
          </template>
          <template v-else>
            <v-icon size="18" :icon="mdiWindowClose" class="opacity-40" />
          </template>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-3 pb-0">
          <v-alert class="word-break" type="error" v-if="!!errorMessage">{{
            errorMessage
          }}</v-alert>
          <v-alert class="word-break" type="success" v-if="!!signInMessage">{{
            signInMessage
          }}</v-alert>
          <v-form ref="loginForm">
            <v-col class="">
              <v-text-field
                type="email"
                density="compact"
                placeholder="Enter email Address"
                v-model="email"
                variant=""
                class="text-black coupon-field-input focus-effect"
                :rules="emailRules"
                :rounded="1"
                color="primary"
                hide-details="auto"
              ></v-text-field>
            </v-col>
            <v-col class="pt-2">
              <v-text-field
                type="password"
                density="compact"
                placeholder="Enter password"
                v-model="password"
                variant=""
                class="text-black coupon-field-input focus-effect"
                :rules="passwordRules"
                :rounded="1"
                color="primary"
                hide-details="auto"
              ></v-text-field>
            </v-col>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-row no-gutters>
            <v-col cols="12" class="px-7">
              <v-btn
                color="primary"
                block
                variant="flat"
                size="large"
                @click="signIn()"
              >
                <v-progress-circular
                  :size="20"
                  color="white"
                  indeterminate
                  v-if="isAuthAccountInProgress"
                ></v-progress-circular>
                &nbsp;SIGN IN
              </v-btn>
            </v-col>
            <v-col cols="12" class="py-5" align="center">
              <span
                class="text-decoration-none cursor-pointer text-primary"
                @click="goToForgetPassword()"
                >Forgot Your Password?</span
              >
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Import environment base URL for canonical links
const baseUrl = import.meta.env.VITE_BASE_URL;

// Import required components and services
import { getStripe } from "@/services/loadStripe"; // Loads Stripe.js instance
import StripeCheckout from "@/components/crane/payment/StripeCheckout.vue"; // Stripe payment form
import CheckoutProductList from "@/components/crane/cart-product/CheckoutProductList.vue"; // Product list in checkout
import SnackbarComponent from "@/components/common/SnackbarComponent.vue"; // Snackbar for notifications
import { createHelpers } from "vuex-map-fields"; // For mapping Vuex fields
import { getCookieByName } from "@/services/auth"; // Utility to get cookies
import { getMagentoRegionId } from "@/services/directory.service"; // Not used here, but imported
import CheckoutPageSkeleton from "@/components/crane/cart-product/CheckoutPageSkeleton.vue"; // Skeleton loader
import countries from "@/services/countries"; // Country/state data
import { mdiChevronDown, mdiWindowClose } from "@mdi/js"; // Icon for order summary
import {
  googleAanalyticsPurchase,
  purchaseIncident,
  googleAanalyticsCheckout,
  googleAanalyticsCheckoutAddShippingInfo,
  googleAanalyticsCheckoutAddPaymentInfo,
  googleAanalyticsPromoCode,
  formatPhone,
  getErrorMessage,
  datadogLogError,
  getShippingArray,
  getCustomOptionValue,
  formatAmount,
} from "@/services/util.service"; // Utility functions
const { cookies } = useCookies();

// Guest quote ID from environment
const crGuestQuoteId = import.meta.env.VITE_GUEST_QUOTE_ID;
import { useCookies } from "vue3-cookies";
const fingerprintCookies = import.meta.env.VITE_FINGERPRINT_COOKIES;
const storedVisitorId = cookies.get(fingerprintCookies);
const { mapFields: mapFieldsAuth } = createHelpers({
  getterType: "auth/getField",
  mutationType: "auth/updateField",
});

export default {
  name: "CheckoutPage",
  data() {
    return {
      mdiChevronDown, // Icon for chevron
      snackbar: {
        show: false, // Snackbar visibility
        message: null, // Snackbar message
        color: null, // Snackbar color
        timeout: 0, // Snackbar timeout
      },
      stripe: null, // Stripe instance
      paymentError: false, // Payment error dialog visibility
      checkoutError: {
        title: "", // Error dialog title
        message: "", // Error dialog message
      },
      isOrderSummaryOpen: false, // Mobile order summary open/close state
      signInForm: false,
      mdiWindowClose,
      valid: true,
      validLogin: false,
      emailRules: [
        (v) => !!v || "Email is required",
        (v) => /.+@.+\..+/.test(v) || "Email must be valid",
      ],
      passwordRules: [
        (v) => !!v || "Password is required",
        (v) => (v && v.length >= 8) || "Password must be at least 8 characters",
      ],
      avalaraAddressResponse: null,
    };
  },
  metaInfo() {
    return {
      titleTemplate: "Checkout",
      title: "Checkout",
      keywords: "Magento, Varien, E-commerce",
      viewport: "width=device-width, initial-scale=1",
      robots: "INDEX,FOLLOW",
      "format-detection": "telephone=no",

      link: [
        {
          rel: "canonical",
          href: `${
            this.canonical ? window.location.href.split("?")[0] : baseUrl
          }`,
        },
      ],
    };
  },
  components: {
    StripeCheckout,
    CheckoutProductList,
    SnackbarComponent,
    CheckoutPageSkeleton,
  },

  computed: {
    ...mapFieldsAuth([
      "one",
      "one.email",
      "one.password",
      "errorMessage",
      "signInMessage",
    ]),
    // Get cart total amount depending on user type
    cartProductTotalAmt() {
      return this.userAuth
        ? this.$store.state.userCartProduct.productTotalAmt
        : this.$store.state.cartProduct.productTotalAmt;
    },
    // Check if user is authenticated
    userAuth() {
      return this.$store.state.auth.userAuth;
    },
    // Get guest quote ID from cookie
    guestQuoteId() {
      return getCookieByName(crGuestQuoteId);
    },
    // Get user info depending on user type
    userInfo() {
      return this.userAuth
        ? this.$store.state.accountOne.one
        : this.$store.state.guestInfo.one;
    },
    // Get cart product items depending on user type
    cartProductsItem() {
      return this.userAuth
        ? this.$store.state.userCartProduct.cartProductList
        : this.$store.state.cartProduct.cartProductList;
    },
    // Get cart products progress state
    cartProductsProgress() {
      return this.userAuth
        ? this.$store.state.userCartProduct.cartProductsProgress
        : this.$store.state.cartProduct.cartProductsProgress;
    },
    // Get estimated shipping methods
    estimatedShippingMethods() {
      return this.userAuth
        ? this.$store.state.accountOne.estimatedShipping
        : this.$store.state.guestInfo.estimatedShipping;
    },
    // Check if checkout is processing (loading states)
    isCheckoutProcessing() {
      const state = this.$store.state;
      const cartTotalAmtProgress = this.userAuth
        ? state.userCartProduct.cartTotalAmtProgress
        : state.cartProduct.cartTotalAmtProgress;
      return (
        state.stripeCheckout.isShippingOptionLoading ||
        state.stripeCheckout.isShippingInformationSubmitting ||
        !!cartTotalAmtProgress
      );
    },
    // Check if payment is processing
    isPaymentProcessing() {
      return this.$store.state.stripeCheckout.isPaymentProcessing;
    },
    // Get guest discount code
    discountGuestCode() {
      return this.$store.state.cartProduct.discountCode;
    },
    // Get user discount code list
    userDiscountCodeList() {
      return this.$store.state.userCartProduct.discountCodeList;
    },
    // Check if coupon is loading
    isCouponLoading() {
      return this.userAuth
        ? this.$store.state.userCartProduct.isCouponLoading
        : this.$store.state.cartProduct.isCouponLoading;
    },
    // Get gift card credit balance
    giftCardCreditBalance() {
      return this.$store.state.storeCredit.giftCardCreditBalance;
    },
    // Get store credit used (for authenticated users)
    storeCreditUsed() {
      if (!this.userAuth) return 0;
      try {
        const totalSegments =
          this.$store.state.userCartProduct.productTotalAmt.totalSegments;
        const customerBalance = totalSegments.find(
          (a) => a.code === "customerbalance"
        );
        return customerBalance?.value ?? 0;
      } catch {
        return 0;
      }
    },
    // Check if all products in cart are gift cards
    isGiftCardProduct() {
      return (
        this.cartProductsItem.length > 0 &&
        this.cartProductsItem.every((item) => item.productType === "giftcard")
      );
    },
    // if auth and account is progress
    isAuthAccountInProgress() {
      const state = this.$store.state;
      return state.auth.inProgress || state.accountOne.inProgress;
    },
  },

  methods: {
    formatAmount,
    // Navigate to shopping/home page
    goToShopping() {
      this.$router.push({ name: "HomePage" });
    },
    // Show error dialog and snackbar
    showError(title, message, snackbarMsg = null) {
      this.checkoutError.title = title;
      this.checkoutError.message = message;
      this.paymentError = true;
      this.snackbar = {
        message: snackbarMsg || message,
        color: "error",
        show: true,
        timeout: 3000,
      };
    },
    // Show success snackbar
    showSuccess(message) {
      this.snackbar = {
        message,
        color: "success",
        show: true,
        timeout: 2000,
      };
    },
    // Set shipping address and update shipping options
    async validateShippingAddress(data) {
      try {
        this.$store.dispatch(
          "stripeCheckout/setValidateShippingAddressLoading",
          true
        );
        const address = data.address;
        const countryData = countries[address.country];
        // Find region by state code
        const region = countryData.available_regions.find(
          (r) => r.code.toUpperCase() === address.state.toUpperCase()
        );
        const fullName = data?.name?.trim() ?? "";
        const [firstname = "", lastname = ""] = fullName.split(/ (.+)/);

        if (region) {
          // Prepare shipping address object
          const validateAddress = {
            regionId: region.id,
            region,
            countryId: address.country,
            postcode: address.postal_code,
            city: address.city,
            streetNoOne: address.line1,
            streetNoTwo: address.line2,
            firstname: firstname,
            lastname: lastname,
            telephone: data?.phone ? formatPhone(data.phone) : "",
          };
          this.avalaraAddressResponse = null;
          this.avalaraAddressResponse = await this.$store.dispatch(
            "accountOne/validateAddressCheckout",
            { address: validateAddress }
          );
          if (typeof this.avalaraAddressResponse == "object") {
            if (this.avalaraAddressResponse[0].error_message) {
              this.avalaraAddressResponse = {
                ...this.avalaraAddressResponse[0],
                error: true,
              };
            } else {
              this.avalaraAddressResponse = this.avalaraAddressResponse[0];
            }
          } else {
            this.avalaraAddressResponse.error = true;
          }
        } else {
        }
      } catch (error) {
        console.error("error in validating address", error);
      } finally {
        // Always stop loading state
        this.$store.dispatch(
          "stripeCheckout/setValidateShippingAddressLoading",
          false
        );
      }
    },
    // Set shipping address and update shipping options
    async setShippingAddress(data) {
      try {
        this.$store.dispatch("stripeCheckout/setShippingOptionLoading", true);
        const address = data.address;
        const countryData = countries[address.country];
        // Find region by state code
        const region = countryData.available_regions.find(
          (r) => r.code.toUpperCase() === address.state.toUpperCase()
        );
        if (region) {
          // Split name into firstname and lastname
          const fullName = data?.name?.trim() ?? "";
          const [firstname = "", lastname = ""] = fullName.split(/ (.+)/);
          // Prepare shipping address object
          const shippingAddress = {
            regionId: region.id,
            region,
            countryId: address.country,
            postcode: address.postal_code,
            city: address.city,
            street: [address.line1, address.line2],
            telephone: data?.phone ? formatPhone(data.phone) : "",
            firstname,
            lastname,
          };
          if (this.userAuth) {
            // Authenticated user: set shipping details and update cart
            await Promise.all([
              this.$store.dispatch(
                "accountOne/setShippingDetails",
                shippingAddress
              ),
              this.$store.dispatch("userCartProduct/cartProductList"),
              this.$store.dispatch("accountOne/setEstimatedShippingOptions"),
            ]);
          } else {
            // Guest user: set shipping details and update cart
            await Promise.all([
              this.$store.dispatch(
                "guestInfo/setShippingDetails",
                shippingAddress
              ),
              this.$store.dispatch(
                "cartProduct/cartProductList",
                this.guestQuoteId
              ),
              this.$store.dispatch(
                "cartProduct/productTotalAmt",
                this.guestQuoteId
              ),
              this.$store.dispatch("guestInfo/setEstimatedShippingOptions", {
                email: data.email,
              }),
            ]);
          }
        } else {
          // Show error if region/state is invalid
          // this.showError(
          //   "Shipping Error",
          //   getErrorMessage(
          //     null,
          //     "The selected state is not available. Please verify your shipping address and try again."
          //   )
          // );
        }
      } catch (error) {
        // { additional: 'Shipping Error' }
        const err =
          error instanceof Error
            ? error
            : new Error(error?.message || JSON.stringify(error));
        datadogLogError(err, { userInfo: this.userInfo, shippingInfo: data });
        // Show error if shipping option fetch fails
        this.showError(
          "Shipping Error",
          getErrorMessage(
            error,
            "Error in fetching shipping options, please check shipping address."
          )
        );
      } finally {
        // Always stop loading state
        this.$store.dispatch("stripeCheckout/setShippingOptionLoading", false);
      }
    },
    // Set shipping method and update totals
    async setShippingInformation({ email, method }) {
      try {
        this.$store.dispatch(
          "stripeCheckout/setShippingInformationSubmittingLoading",
          true
        );
        if (this.userAuth) {
          // Authenticated user: set shipping info and update totals
          await this.$store.dispatch("accountOne/setUserShippingInformation", {
            method,
            email,
          });
          await this.$store.dispatch("userCartProduct/productTotalAmt");
        } else {
          // Guest user: set shipping info and update totals
          await this.$store.dispatch("guestInfo/setGuestShippingInformation", {
            method,
            email,
          });
          await this.$store.dispatch(
            "cartProduct/productTotalAmt",
            this.guestQuoteId
          );
        }
        googleAanalyticsCheckoutAddShippingInfo(
          "add_shipping_info",
          this.cartProductsItem,
          method
        );
      } catch (error) {
        const err =
          error instanceof Error
            ? error
            : new Error(error?.message || JSON.stringify(error));
        datadogLogError(err, { userInfo: this.userInfo, email, method });
        // Show error if shipping info fails
        this.showError(
          "Shipping Error",
          getErrorMessage(
            error,
            "Error in setting shipping address, please check and try again"
          )
        );
      } finally {
        // Always stop loading state
        this.$store.dispatch(
          "stripeCheckout/setShippingInformationSubmittingLoading",
          false
        );
      }
    },
    // Process payment using Stripe and handle order placement
    async processPayment({
      email,
      billingDetails,
      paymentMethod,
      paymentMethodType,
      paymentProcessed = false,
      retries = 0,
    }) {
      try {
        billingDetails.phone = formatPhone(billingDetails.phone);
        this.$store.dispatch("stripeCheckout/setPaymentProcessing", true);
        let order, orderId;
        if (this.userAuth) {
          // Authenticated user: process payment via accountOne
          const resp = await this.$store.dispatch("accountOne/processPayment", {
            email,
            billingDetails,
            paymentMethod: {
              method: paymentMethodType,
              additional_data: { payment_method: paymentMethod.id },
            },
            paymentProcessed,
          });
          order = resp.order;
          orderId = resp.orderId;
        } else {
          // Guest user: process payment via guestInfo
          const resp = await this.$store.dispatch("guestInfo/processPayment", {
            email,
            billingDetails,
            paymentMethod: {
              method: paymentMethodType,
              additional_data: {
                payment_method: paymentMethod.id,
              },
            },
            paymentProcessed,
          });
          order = resp.order;
          orderId = resp.orderId;
        }

        googleAanalyticsCheckoutAddPaymentInfo("add_payment_info", order);
        googleAanalyticsPurchase(order, order.incrementId);
        purchaseIncident(order, order.incrementId);

        // Process order after payment
        await this.processOrder({ order, orderId });
        // Redirect to order success page
        this.$router.push({
          name: "OrderSuccess",
          params: { orderNumber: orderId },
        });
      } catch (error) {
        const err =
          error instanceof Error
            ? error
            : new Error(error?.message || JSON.stringify(error));
        datadogLogError(err, {
          userInfo: this.userInfo,
          email,
          billingDetails,
          ...error?.extraInfo,
        });
        // Handle Stripe authentication required error
        const errorMsg = getErrorMessage(error, "Payment processing failed");
        if (errorMsg.startsWith("Authentication Required")) {
          this.snackbar = {
            message: "Authentication Required",
            color: "error",
            show: true,
            timeout: 3000,
          };
          const clientSecret = errorMsg.split("Authentication Required: ")[1];
          // Handle next Stripe action (e.g., 3D Secure)
          this.handleNextAction(clientSecret, async (nextError) => {
            if (nextError) {
              this.$store.dispatch(
                "stripeCheckout/setPaymentProcessing",
                false
              );
              this.showError(
                "Payment Error",
                getErrorMessage(nextError, "Payment processing failed")
              );
            } else if (retries < 5) {
              // Retry payment up to 5 times
              await this.processPayment({
                email,
                billingDetails,
                paymentMethod,
                paymentProcessed: false,
                retries: retries + 1,
              });
            }
          });
        } else {
          // Show error for other payment failures
          this.showError("Payment Error", errorMsg);
        }
      } finally {
        // Always stop payment processing state
        this.$store.dispatch("stripeCheckout/setPaymentProcessing", false);
      }
    },
    // Handle express payment completion (e.g., Apple Pay)
    async handleExpressPaymentCompletion({ order, orderId }) {
      try {
        await this.processOrder({ order, orderId });
        this.$router.push({
          name: "OrderSuccess",
          params: { orderNumber: orderId },
        });
      } catch (error) {
        const err =
          error instanceof Error
            ? error
            : new Error(error?.message || JSON.stringify(error));
        datadogLogError(err, { userInfo: this.userInfo });
        this.showError(
          "Order Process failed",
          getErrorMessage(error, "Order Process failed")
        );
      }
    },
    // Process order after payment (handles custom logic for builder orders)
    async processOrder({ order, orderId }) {
      if (!order?.items) return;
      let shippingAddress = [];
      let ccid = "";
      let webOrderNumber = order.incrementId;
      let magentoOrderNumber = orderId;
      for (const obj of order.items) {
        if (obj.extensionAttributes) {
          ccid = obj.extensionAttributes.ccid;
          if (ccid) {
            try {
              shippingAddress =
                order.extensionAttributes.shippingAssignments[0].shipping
                  .address;
              await this.$store.dispatch("accountOne/setCCOrder", {
                orderNumber: ccid,
                shippingAddress,
                webOrderNumber,
                estimatedShipping: this.estimatedShipping,
              });
            } catch (error) {
              const err =
                error instanceof Error
                  ? error
                  : new Error(error?.message || JSON.stringify(error));
              datadogLogError(err, { userInfo: this.userInfo, ccid });
              this.showError(
                "Order Error",
                getErrorMessage(error, "Failed to set CC order")
              );
            }
          }
          let builderSessionOne = null;
          let designProjectOne = null;
          try {
            // Check for builder session custom attribute
            if (obj?.extensionAttributes?.customAttributes?.length > 0) {
              builderSessionOne = obj.extensionAttributes.customAttributes
                .map((attr) => {
                  try {
                    return JSON.parse(attr);
                  } catch {
                    return null;
                  }
                })
                .find(
                  (att) => att && att.title && att.title === "BuilderSessionId"
                );
              if (builderSessionOne) {
                // Place builder order if session found
                await this.placeCraneOrder(
                  order,
                  obj,
                  webOrderNumber,
                  magentoOrderNumber
                );
              }
              designProjectOne = obj.extensionAttributes.customAttributes
                .map((attr) => {
                  try {
                    return JSON.parse(attr);
                  } catch {
                    return null;
                  }
                })
                .find(
                  (att) => att && att.title && att.title === "DESIGN_PROJECT_ID"
                );
              if (designProjectOne) {
                await this.placeExpressOrder(
                  order,
                  obj,
                  webOrderNumber,
                  magentoOrderNumber
                );
              }
            }
          } catch (error) {
            error.extraInfo = { builderSessionOne, designProjectOne };
            this.showError(
              "Order Error",
              getErrorMessage(error, "Failed to process order")
            );
            throw error; // rethrow the error with extra data
          }
        }
      }
    },
    // Place builder order for custom products
    async placeCraneOrder(resp, obj, webOrderNumber, magentoOrderNumber) {
      try {
        // Prepare shipping address for builder order
        let shipArray = getShippingArray({
          resp,
          obj,
          webOrderNumber,
        });

        let builderSessionOne = getCustomOptionValue({
          obj,
          customOptionName: "BuilderSessionId",
        });

        if (!builderSessionOne?.optionValue) return;

        // Fetch builder session details
        const sessionId = builderSessionOne.optionValue;
        const sessionOneDetails = await this.$store.dispatch(
          "sessionOne/fetchOne",
          {
            sessionId,
          }
        );

        const sessionData = sessionOneDetails?.response?.[0];
        if (!sessionData) return;

        // Prepare and update order data
        const orderData = {
          m2_order_id: String(magentoOrderNumber),
          m2_order_item_id: String(obj?.itemId),
          m2_order_po_id: String(webOrderNumber),
          ship_array: shipArray,
        };
        const quoteData = { qty: obj.qtyOrdered };
        await this.$store.dispatch("sessionOne/updateSessionOne", {
          sessionId,
          property: {
            order_data: {
              ...sessionData.orderData,
              ...orderData,
            },
            quote_data: {
              ...sessionData.quoteData,
              ...quoteData,
            },
            order_state: "ordered",
          },
        });

        // Track purchase event in Datadog RUM
        const sku = obj.sku;
        const qty = Number(obj.qtyOrdered) || 0;
        const price = Number(obj.price) || 0;

        if (window?.DD_RUM?.onReady && sku) {
          window.DD_RUM.onReady(() => {
            window.DD_RUM.addAction("studio_session", {
              session_id: sessionId,
              po_id: webOrderNumber,
              session_state: "purchase",
              sku,
              price: qty * price,
            });
          });
        }
      } catch (error) {
        console.error("❌ Error placing studio order:", error);
        throw error;
      }
    },
    async placeExpressOrder(resp, obj, webOrderNumber, magentoOrderNumber) {
      try {
        // --- 1️⃣ Prepare shipping data ---
        const shipArray = getShippingArray({ resp, obj, webOrderNumber });

        // --- 2️⃣ Fetch design project custom option ---
        const designProject = getCustomOptionValue({
          obj,
          customOptionName: "DESIGN_PROJECT_ID",
        });
        console.log("designProject:", designProject);

        const projectId = designProject?.optionValue;
        if (!projectId) return;

        // --- 3️⃣ Fetch builder sessions by designer project ID ---
        const builderSessions = await this.$store.dispatch(
          "sessionOne/fetchByDesignProjectId",
          { designProjectId: projectId }
        );
        const builderSession = builderSessions?.[0];
        if (!builderSession) return;

        // --- 4️⃣ Prepare session update data ---
        const quoteData = { qty: obj.qtyOrdered };

        const userData = resp?.customerId
          ? {
              fingerprint_id: storedVisitorId,
              customer_id: resp.customerId,
              customer_name: `${resp?.customerFirstname ?? ""} ${
                resp?.customerLastname ?? ""
              }`.trim(),
            }
          : {
              guest_name: "Guest",
              fingerprint_id: storedVisitorId,
            };

        const orderData = {
          m2_order_id: String(magentoOrderNumber),
          m2_order_item_id: String(obj?.itemId),
          m2_order_po_id: String(webOrderNumber),
          ship_array: shipArray,
        };

        // --- 5️⃣ Update builder session ---
        await this.$store.dispatch("sessionOne/updateSessionOne", {
          sessionId: builderSession.id,
          property: {
            order_data: {
              ...builderSession.orderData,
              ...orderData,
            },
            quote_data: quoteData,
            user_data: userData,
            order_state: "prepare_svg",
          },
        });

        // --- 6️⃣ Track in Datadog RUM ---
        const sku = obj.sku;
        const qty = Number(obj.qtyOrdered) || 0;
        const price = Number(obj.price) || 0;

        if (window?.DD_RUM?.onReady && sku) {
          window.DD_RUM.onReady(() => {
            window.DD_RUM.addAction("designer_project", {
              designer_project_id: projectId,
              po_id: webOrderNumber,
              state: "purchase",
              sku,
              price: qty * price,
            });
          });
        }
      } catch (error) {
        console.error("❌ Error placing express order:", error);
        throw error; // propagate for higher-level handling
      }
    },

    // Handle Stripe next action (e.g., 3D Secure authentication)
    handleNextAction(intentId, callback) {
      try {
        const isPaymentIntent = intentId.startsWith("pi_");
        const isSetupIntent = intentId.startsWith("seti_");
        // Handle intent result
        const handleIntent = (result) => {
          if (result.error)
            return callback(
              getErrorMessage(result.error, "Payment action failed")
            );
          const intent = result.paymentIntent || result.setupIntent;
          const requiresActionStatuses = [
            "requires_action",
            "requires_source_action",
          ];
          if (
            requiresActionStatuses.includes(intent.status) &&
            intent.next_action
          ) {
            if (intent.next_action.type === "verify_with_microdeposits") {
              // Redirect to microdeposits verification if required
              window.location =
                intent.next_action.verify_with_microdeposits.hosted_verification_url;
            } else {
              // Handle other next actions (e.g., 3D Secure)
              this.stripe
                .handleNextAction({
                  clientSecret: intent.client_secret,
                })
                .then((result) => {
                  if (result?.error) {
                    return callback(
                      getErrorMessage(result.error, "Payment action failed")
                    );
                  }
                  return callback();
                });
            }
          } else {
            return callback();
          }
        };
        // Retrieve intent from Stripe
        if (isPaymentIntent) {
          this.stripe.retrievePaymentIntent(intentId).then(handleIntent);
        } else if (isSetupIntent) {
          this.stripe.retrieveSetupIntent(intentId).then(handleIntent);
        } else {
          throw new Error("Invalid intent ID");
        }
      } catch (e) {
        callback(getErrorMessage(e, "Payment action failed"));
      }
    },
    // Apply coupon code for guest user
    async checkGuestCouponCode(code) {
      try {
        await this.$store.dispatch("cartProduct/applyDiscountCode", {
          discountTextCode: code,
          guestQuoteId: this.guestQuoteId,
        });
        googleAanalyticsPromoCode("promo_code", code);
        await this.$store.dispatch(
          "cartProduct/productTotalAmt",
          this.guestQuoteId
        );
        this.showSuccess("Your coupon was successfully applied");
      } catch (error) {
        this.showError(
          "Coupon Error",
          getErrorMessage(error, "Failed to apply coupon")
        );
      }
    },
    // Remove coupon code for guest user
    async removeGuestCouponCode(code) {
      try {
        await this.$store.dispatch("cartProduct/removeDiscountCode", {
          discountTextCode: code,
          guestQuoteId: this.guestQuoteId,
        });
        this.showSuccess("Your coupon was successfully removed");
        await this.$store.dispatch(
          "cartProduct/productTotalAmt",
          this.guestQuoteId
        );
      } catch (error) {
        this.showError(
          "Coupon Error",
          getErrorMessage(error, "Failed to remove coupon")
        );
      }
    },
    // Apply coupon code for authenticated user
    async checkUserCouponCode(code) {
      try {
        await this.$store.dispatch("userCartProduct/applyDiscountCode", code);
        googleAanalyticsPromoCode("promo_code", code);
        this.showSuccess("Your coupon was successfully applied");
        await this.$store.dispatch("userCartProduct/productTotalAmt");
      } catch (error) {
        this.showError(
          "Coupon Error",
          getErrorMessage(error, "Failed to apply coupon")
        );
      }
    },
    // Remove coupon code for authenticated user
    async removeUserCouponCode(item) {
      try {
        await this.$store.dispatch("userCartProduct/removeDiscountCode", item);
        this.showSuccess("Your coupon was successfully removed");
        await this.$store.dispatch("userCartProduct/productTotalAmt");
      } catch (error) {
        this.showError(
          "Coupon Error",
          getErrorMessage(error, "Failed to remove coupon")
        );
      }
    },
    // Close payment error dialog
    closePaymentErrorDiloag() {
      this.paymentError = false;
      this.checkoutError = {};
    },
    // Apply store credit for authenticated user
    async useStoreCredit() {
      try {
        await this.$store.dispatch("storeCredit/applyStoreCredit");
        await this.$store.dispatch("userCartProduct/productTotalAmt");
      } catch (error) {
        this.showError(
          "Store Credit Error",
          getErrorMessage(error, "Error using store credit")
        );
      }
    },
    openSign(subscribeToNewsletter) {
      this.$store.dispatch("auth/newUserLogin");
      this.subscribeToNewsletter = subscribeToNewsletter;
      this.signInForm = true;
    },
    async signIn() {
      let validate = await this.$refs.loginForm.validate();
      if (validate && validate.valid) {
        this.$store.dispatch("auth/changeRedirectFlag", false);
        let loginnResponse = await this.$store.dispatch("auth/login");
        if (loginnResponse) {
          if (this.subscribeToNewsletter) {
            await this.$store.dispatch("accountOne/basicInfo");
            await this.$store.dispatch(
              "accountOne/updateSubscription",
              this.subscribeToNewsletter
            );
          }
          this.$store.dispatch("auth/changeRedirectFlag", true);
          window.open(`/checkout`, "_self");
        }
      }
    },
    goToForgetPassword() {
      window.open(`/gate/forgot-password`, "_self");
    },
    closeSignInForm() {
      this.signInForm = false;
      this.$store.dispatch("auth/changeRedirectFlag", true);
    },
  },

  async created() {
    // On component creation, initialize Stripe and fetch user/cart info
    try {
      this.stripe = await getStripe();
      if (this.userAuth) {
        // Authenticated user: fetch basic info and coupon list
        await this.$store.dispatch("accountOne/basicInfo");
        this.$store.dispatch("userCartProduct/fetchCartCouponList");
        if (this.userInfo) {
          this.$store
            .dispatch(
              "storeCredit/fetchGiftCardCreditBalance",
              this.userInfo.id
            )
            .catch(() => {
              this.showStoreCredit = false;
            });
        }
      } else if (this.guestQuoteId) {
        // Guest user: fetch basic info and coupon
        this.$store.dispatch("guestInfo/basicInfo");
        this.$store.dispatch("cartProduct/fetchCartCoupon", this.guestQuoteId);
      }
      googleAanalyticsCheckout("begin_checkout", {
        items: this.cartProductsItem,
      });
    } catch (error) {
      const err =
        error instanceof Error
          ? error
          : new Error(error?.message || JSON.stringify(error));

      datadogLogError(err, { userInfo: this.userInfo });
      this.showError(
        "Checkout Error",
        getErrorMessage(error, "Failed to initialize checkout")
      );
    }
  },
};
</script>

<!-- Import SCSS styles for the checkout page -->
<style src="./checkout_page.scss" lang="scss" />
